import { z } from "zod";
import {
  insightItemSchema,
  animationStyleSchema,
  animationSpeedSchema,
} from "./item.schema.js";
import {
  overlayOrArraySchema,
  colorPaletteSchema,
  backgroundMediaConfigSchema,
} from "./overlay.schema.js";

/**
 * Insight group - represents one swipe screen with sequentially animated items
 */
export const insightGroupSchema = z
  .object({
    id: z
      .string()
      .optional()
      .describe(
        "Unique identifier. Optional for backward-compat with existing narratives, but expected going forward: the Dispatch MCP update_group tool targets a group by this id. New narratives (pipeline + skill-authored) should always set it.",
      ),
    reasoning: z
      .string()
      .optional()
      .describe(
        "Internal documentation (1-3 sentences) explaining why this group exists — a trust mechanism, not rendered. Optional so pipeline-generated narratives can omit it.",
      ),
    animationStyle: animationStyleSchema
      .optional()
      .describe(
        "Optional animation style override for every item in this group.",
      ),
    animationSpeed: animationSpeedSchema
      .optional()
      .describe(
        "Optional animation speed multiplier override for every item in this group: 1 = normal, 2 = twice as fast, 0.5 = half speed.",
      ),
    contentAuthor: z
      .string()
      .optional()
      .describe(
        'Author attribution: omit or "ai" for the AI badge, any other string for a human name badge (e.g. "Sarah Blackwell").',
      ),
    autoAdvance: z
      .boolean()
      .optional()
      .describe(
        "When true, auto-advance to the next page after the last content finishes animating (requires format=video).",
      ),
    autoScroll: z
      .boolean()
      .optional()
      .describe(
        "When true, auto-scroll content so newly revealed items stay in view as they animate in. Overrides section-level autoScroll.",
      ),
    items: z
      .array(insightItemSchema)
      .min(1)
      .describe("Content items revealed in order (minimum 1 required)."),
    overlay: overlayOrArraySchema
      .optional()
      .describe("Optional animation overlay (single or array) for this group."),
  })
  .describe(
    "An insight group: one swipe screen whose items animate in sequentially.",
  );

/**
 * Narrative section - represents one story chapter
 */
export const narrativeSectionSchema = z
  .object({
    id: z
      .string()
      .describe(
        'Unique section identifier (reserved ids: "intro", "table_of_contents").',
      ),
    label: z.string().optional().describe("Display label for the section."),
    icon: z
      .string()
      .optional()
      .describe(
        'Font Awesome icon as "<prefix> <name>" (e.g. "fausb chart-simple"); omit for no icon.',
      ),
    reasoning: z
      .string()
      .optional()
      .describe(
        "Internal documentation (1-2 sentences) explaining the section's role in the narrative arc — a trust mechanism, not rendered. Optional so pipeline-generated narratives can omit it.",
      ),
    colorPalette: colorPaletteSchema
      .optional()
      .describe(
        "Optional inline color palette for the gradient background. When absent the runtime assigns a random palette.",
      ),
    disableInterstitial: z
      .boolean()
      .optional()
      .describe(
        "Set true to suppress the section interstitial for this section.",
      ),
    autoScroll: z
      .boolean()
      .optional()
      .describe(
        "When true, every group in this section auto-scrolls so newly revealed content stays in view (each group can still override).",
      ),
    groups: z
      .array(insightGroupSchema)
      .min(1)
      .describe(
        "Insight groups — the swipe screens of this section (minimum 1).",
      ),
    backgroundMusic: z
      .union([
        z
          .string()
          .describe(
            "Path to an audio file. The common form — plays at default volume (0.3), looping.",
          ),
        z
          .object({
            src: z.string().describe("Path to the audio file."),
            volume: z
              .number()
              .min(0)
              .max(1)
              .optional()
              .describe("Max volume, 0-1 (default: 0.3)."),
            loop: z
              .boolean()
              .optional()
              .describe("Whether to loop the track (default: true)."),
          })
          .describe(
            "Object form — only needed to override volume or looping for this track.",
          ),
      ])
      .optional()
      .describe(
        "Background music for the section: a path string (the common form), or an object when you need to override volume or looping.",
      ),
    backgroundMedia: z
      .union([
        z
          .string()
          .describe(
            "Path to an image or video file. The common form — full opacity with default Ken Burns on images.",
          ),
        backgroundMediaConfigSchema,
      ])
      .optional()
      .describe(
        "Background image/video for the section: a path string (the common form), or a config object when you need persist, opacity, or Ken Burns control.",
      ),
    overlay: overlayOrArraySchema
      .optional()
      .describe(
        "Optional animation overlay (single or array) for this section.",
      ),
  })
  .describe("A narrative section: one story chapter made of insight groups.");

// Export inferred types
export type InsightGroup = z.infer<typeof insightGroupSchema>;
export type NarrativeSection = z.infer<typeof narrativeSectionSchema>;
